import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Trash2, ShieldCheck, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { Avatar } from './Profile';

const EMPTY = { username: '', email: '', password: '', full_name: '', phone: '', designation: '', department: '', role: 'operator' };

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: authApi.getUsers,
    enabled: currentUser?.role === 'admin',
  });

  const createMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: () => { qc.invalidateQueries(['users']); toast.success('User created successfully'); setModal(false); setForm(EMPTY); },
    onError: (e) => toast.error(e.error || 'Failed to create user'),
  });

  const deleteMutation = useMutation({
    mutationFn: authApi.deleteUser,
    onSuccess: () => { qc.invalidateQueries(['users']); toast.success('User deleted'); },
    onError: (e) => toast.error(e.error || 'Failed to delete'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => authApi.setUserStatus(id, status),
    onSuccess: () => { qc.invalidateQueries(['users']); toast.success('Status updated'); },
    onError: (e) => toast.error(e.error || 'Failed to update status'),
  });

  const submit = (e) => { e.preventDefault(); createMutation.mutate(form); };

  if (currentUser?.role !== 'admin') {
    return (
      <>
        <div className="topbar"><span className="topbar-title">User Management</span></div>
        <div className="page-content">
          <div className="empty-state" style={{ marginTop: 60 }}>
            <ShieldCheck style={{ width: 40, height: 40 }} />
            <p style={{ marginTop: 12 }}>Admin access required to manage users.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">User Management</span>
        <button className="btn btn-primary" onClick={() => setModal(true)}><UserPlus size={14} /> Add User</button>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <ShieldCheck size={15} />
            <span className="card-title">System Users ({users.length})</span>
          </div>
          <div className="table-wrap">
            {isLoading ? <div className="spinner" /> : (
              <table>
                <thead><tr>
                  <th>User</th><th>Username</th><th>Email</th><th>Designation</th><th>Role</th><th>Last Login</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar user={u} size={32} />
                          <span style={{ fontWeight: 500 }}>{u.full_name || u.username}</span>
                        </div>
                      </td>
                      <td className="mono">{u.username}</td>
                      <td>{u.email}</td>
                      <td>{u.designation || '—'}{u.department ? ` / ${u.department}` : ''}</td>
                      <td>
                        <span className={`badge badge-${u.role === 'admin' ? 'approved' : u.role === 'manager' ? 'issued' : 'active'}`} style={{ textTransform: 'capitalize' }}>
                          {u.role}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never'}
                      </td>
                      <td><span className={`badge badge-${u.status}`}>{u.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn-icon"
                            title={u.status === 'active' ? 'Deactivate' : 'Activate'}
                            disabled={u.id === currentUser.id}
                            onClick={() => statusMutation.mutate({ id: u.id, status: u.status === 'active' ? 'inactive' : 'active' })}
                          >
                            {u.status === 'active' ? <ToggleRight size={14} style={{ color: 'var(--accent)' }} /> : <ToggleLeft size={14} />}
                          </button>
                          <button
                            className="btn-icon danger"
                            disabled={u.id === currentUser.id}
                            onClick={() => { if (window.confirm(`Delete user "${u.username}"?`)) deleteMutation.mutate(u.id); }}
                          >
                            <Trash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Add New User</h2>
              <button className="btn btn-sm" onClick={() => setModal(false)}>✕</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Username *</label>
                    <input className="form-control" required value={form.username} onChange={e => set('username', e.target.value)} placeholder="e.g. john.doe" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input className="form-control" type="email" required value={form.email} onChange={e => set('email', e.target.value)} placeholder="john@company.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <input className="form-control" type="password" required value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min 6 characters" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input className="form-control" value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Full name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Mobile number" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input className="form-control" value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="e.g. Store Manager" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <input className="form-control" value={form.department} onChange={e => set('department', e.target.value)} placeholder="e.g. Warehouse" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
                      <option value="operator">Operator</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating…' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}